/**
 * Filtros de los listados de la tienda (resultados de busqueda, categoria y
 * linea): de la URL a un objeto y de vuelta, y la lista de los que estan
 * puestos.
 *
 * Como en la tabla de productos del backoffice, la URL es la unica fuente: un
 * filtro sobrevive a recargar, a entrar a una ficha y volver, y el enlace se
 * puede mandar por WhatsApp tal cual. Los nombres son los de la query del
 * backend, con las listas como parametro repetido (`?marca=a&marca=b`).
 */
import type { FacetasBusqueda, FiltrosBusqueda, OrdenCatalogo } from '@elcarpintero/compartido';

import type { Parametros } from '../servicios/cliente-api';
import { pesos } from './formato';
import { cantidad, compactar, lista, unoDe } from './parametros-url';
import type { FiltroActivo as FiltroActivoDe } from './parametros-url';

export const ORDENES_BUSQUEDA: ReadonlyArray<{ valor: OrdenCatalogo; texto: string }> = [
  { valor: 'relevancia', texto: 'Más relevantes' },
  { valor: 'precio_asc', texto: 'Menor precio' },
  { valor: 'precio_desc', texto: 'Mayor precio' },
  { valor: 'nombre', texto: 'Nombre (A a Z)' },
];

const VALORES_ORDEN = ORDENES_BUSQUEDA.map((opcion) => opcion.valor);

export function leerFiltros(parametros: URLSearchParams): FiltrosBusqueda {
  const orden = unoDe(parametros.get('orden'), VALORES_ORDEN);
  return compactar({
    categorias: lista(parametros, 'categoria'),
    marcas: lista(parametros, 'marca'),
    precioMin: cantidad(parametros.get('precioMin')),
    precioMax: cantidad(parametros.get('precioMax')),
    conStock: parametros.get('conStock') === 'true' ? true : undefined,
    enOferta: parametros.get('enOferta') === 'true' ? true : undefined,
    // La relevancia es el orden por defecto: no se escribe.
    orden: orden !== 'relevancia' ? orden : undefined,
  });
}

/** Los filtros con los nombres de la query, tal cual los espera la API. */
export function parametrosDe(filtros: FiltrosBusqueda): Parametros {
  return {
    categoria: filtros.categorias,
    marca: filtros.marcas,
    precioMin: filtros.precioMin,
    precioMax: filtros.precioMax,
    conStock: filtros.conStock,
    enOferta: filtros.enOferta,
    orden: filtros.orden,
  };
}

/** La query de la pagina: el termino primero, si es una busqueda, y despues los filtros. */
export function escribirFiltros(filtros: FiltrosBusqueda, termino?: string): URLSearchParams {
  const parametros = new URLSearchParams(termino !== undefined ? { termino } : {});
  for (const [clave, valor] of Object.entries(parametrosDe(filtros))) {
    if (valor === undefined) continue;
    if (typeof valor === 'object') valor.forEach((elemento) => parametros.append(clave, elemento));
    else parametros.set(clave, String(valor));
  }
  return parametros;
}

/**
 * La pagina de la URL (`?pagina=`), donde empieza el listado de una categoria
 * o una linea. Lo que no es una pagina valida es la primera.
 */
export function leerPagina(parametros: URLSearchParams): number {
  const pagina = cantidad(parametros.get('pagina'));
  return pagina !== undefined && pagina >= 1 ? pagina : 1;
}

/** La ruta de un listado con sus filtros puestos (sin la pagina: esa la agrega `conPagina`). */
export function conFiltros(ruta: string, filtros: FiltrosBusqueda): string {
  const query = escribirFiltros(filtros).toString();
  return query.length > 0 ? `${ruta}?${query}` : ruta;
}

/** Identidad de los filtros, para saber cuando hay que volver a pedir la primera pagina. */
export function claveFiltros(filtros: FiltrosBusqueda): string {
  return escribirFiltros(filtros).toString();
}

/** Aplica un cambio y devuelve el objeto limpio: una lista vacia es no filtrar por ella. */
export function conCambio(
  filtros: FiltrosBusqueda,
  cambio: Partial<FiltrosBusqueda>,
): FiltrosBusqueda {
  const siguiente = { ...filtros, ...cambio };
  for (const clave of ['categorias', 'marcas'] as const) {
    if (siguiente[clave]?.length === 0) siguiente[clave] = undefined;
  }
  return compactar(siguiente);
}

/** Limpiar todo quita los filtros pero respeta el orden: ordenar no es filtrar. */
export function sinFiltros(filtros: FiltrosBusqueda): FiltrosBusqueda {
  return compactar({ orden: filtros.orden });
}

export type FiltroActivo = FiltroActivoDe<FiltrosBusqueda>;

function textoPrecio({ precioMin: minimo, precioMax: maximo }: FiltrosBusqueda): string {
  if (minimo !== undefined && maximo !== undefined) {
    return `Entre ${pesos(minimo)} y ${pesos(maximo)}`;
  }
  if (minimo !== undefined) return `Desde ${pesos(minimo)}`;
  return `Hasta ${pesos(maximo ?? 0)}`;
}

/**
 * Un chip por filtro puesto. Categoria y marca van uno por valor: quitar
 * "Fresas rectas" no tiene por que quitar tambien "Fresas de perfil".
 */
export function filtrosActivos(
  filtros: FiltrosBusqueda,
  facetas: FacetasBusqueda | null,
): FiltroActivo[] {
  const activos: FiltroActivo[] = [];
  const agregar = (clave: string, texto: string, cambio: Partial<FiltrosBusqueda>) => {
    activos.push({ clave, texto, sinEste: conCambio(filtros, cambio) });
  };
  const nombreCategoria = new Map(facetas?.categorias.map((opcion) => [opcion.slug, opcion.nombre]));

  for (const slug of filtros.categorias ?? []) {
    agregar(`categoria-${slug}`, nombreCategoria.get(slug) ?? slug, {
      categorias: filtros.categorias?.filter((otra) => otra !== slug),
    });
  }
  for (const marca of filtros.marcas ?? []) {
    agregar(`marca-${marca}`, `Marca: ${marca}`, {
      marcas: filtros.marcas?.filter((otra) => otra !== marca),
    });
  }
  if (filtros.precioMin !== undefined || filtros.precioMax !== undefined) {
    agregar('precio', textoPrecio(filtros), { precioMin: undefined, precioMax: undefined });
  }
  if (filtros.conStock === true) agregar('conStock', 'Con stock', { conStock: undefined });
  if (filtros.enOferta === true) agregar('enOferta', 'En oferta', { enOferta: undefined });

  return activos;
}
